import { create } from 'zustand'
import { ApiError, onAuthError } from '@/api/client'
import { authService, type CurrentUser } from '@/services/authService'
import { hasPermission, type Permission } from '@shared/permissions'

type Status = 'unknown' | 'authenticated' | 'anonymous'

interface AuthState {
  status: Status
  user: CurrentUser | null
  permissionSet: Set<string>
  /** Why the user was sent back to the login screen (shown as a notice). */
  signOutReason: 'expired' | 'evicted' | null
  bootstrap: () => Promise<void>
  login: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
  can: (needed: Permission | Permission[] | undefined) => boolean
}

export const useAuth = create<AuthState>()((set, get) => ({
  status: 'unknown',
  user: null,
  permissionSet: new Set(),
  signOutReason: null,

  bootstrap: async () => {
    try {
      const user = await authService.me()
      set({ status: 'authenticated', user, permissionSet: new Set(user.permissions) })
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'OFFLINE' || err.code === 'NETWORK_ERROR') && get().user) return
      set({ status: 'anonymous', user: null, permissionSet: new Set() })
    }
  },

  login: async (username, password) => {
    const user = await authService.login(username, password)
    set({ status: 'authenticated', user, permissionSet: new Set(user.permissions), signOutReason: null })
  },

  logout: async () => {
    await authService.logout().catch(() => undefined)
    set({ status: 'anonymous', user: null, permissionSet: new Set(), signOutReason: null })
  },

  refresh: async () => {
    const user = await authService.me()
    set({ user, permissionSet: new Set(user.permissions) })
  },

  can: (needed) => hasPermission(get().permissionSet, needed),
}))

/*
 * The session is re-read whenever the tab comes back to the front (at most once a minute).
 *
 * Page access is carried on the session, so a change made in صلاحيات المستخدمين used to need the person to refresh
 * before it showed — and nobody does that, which looked exactly like the change had not worked (owner, 2026-09-26:
 * "بردو ظاهر ف سايد بار حاجات ملهاش صلاحيات"). Switching back to the tab is enough now.
 */
if (typeof window !== 'undefined') {
  let lastAt = 0
  const freshen = () => {
    if (document.visibilityState !== 'visible') return
    const now = Date.now()
    if (now - lastAt < 60_000) return
    lastAt = now
    if (useAuth.getState().status === 'authenticated') void useAuth.getState().refresh().catch(() => undefined)
  }
  window.addEventListener('focus', freshen)
  document.addEventListener('visibilitychange', freshen)
}

// A 401 anywhere in the app ends the local session.
onAuthError((err) => {
  if (useAuth.getState().status !== 'authenticated') return
  useAuth.setState({ status: 'anonymous', user: null, permissionSet: new Set(), signOutReason: err.code === 'SESSION_EVICTED' ? 'evicted' : 'expired' })
})

export const useCan = (needed: Permission | Permission[] | undefined) => useAuth((s) => hasPermission(s.permissionSet, needed))
